// v10/src/components/admin/AdminSubscription.js
// v1.5 — Fix i18n: useTranslation hook agregado, PLAN_FEATURES convertido a función para evitar t() en scope de módulo

import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  CreditCard, Check, X, Loader, Zap, Crown,
  AlertCircle, ArrowRight, Shield, CheckCircle,
  Clock, Mail, MessageCircle, DollarSign,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import api from '../../services/apiService';

// ⚠️ PLAN_FEATURES usa t() para algunos labels, por eso se convierte en función.
// No puede ser un const a nivel de módulo porque t() solo existe dentro del componente
// (lo provee useTranslation). Tener t() acá afuera causaba ReferenceError al cargar el módulo.
const getPlanFeatures = (t) => ({
  trial: [
    'Checklists prevuelo ilimitados',
    'Voz Valentina IA (5.000 caracteres/mes)',
    'Hasta 5 pilotos',
    '1 aeronave',
    t('plan_basic_hobbs'),
    t('plan_basic_reports'),
  ],
  basic: [
    'Flota de aeronaves ilimitada',
    'Usuarios ilimitados',
    'Voz Valentina IA (15.000 caracteres/mes)',
    t('plan_pro_hobbs'),
    t('plan_basic_reports'),
    'Historial de inspecciones',
    t('plan_pro_users'),
  ],
  pro: [
    'Todo de Basic',
    'Voz Valentina IA ilimitada',
    'Checklist pre-aterrizaje con voz',
    'Squawks y control AOG avanzado',
    'Firma digital en PDF',
    'Historial ilimitado',
    'Libro de vuelos digital',
    'Directorio de proveedores',
    'Acceso QR por aeronave',
  ],
  enterprise: [
    'Todo de Pro',
    'Flow Mode para pilotos avanzados',
    'ICAO English Trainer',
    'Emergency Procedure Trainer',
    'Material educativo avanzado',
    t('plan_enterprise_export'),
  ],
});

// PLAN_NOT_INCLUDED no usa t(), así que puede quedar como const normal a nivel de módulo
const PLAN_NOT_INCLUDED = {
  trial: [
    'Squawks y control AOG',
    'Firma digital',
    'Libro de vuelos',
    'Acceso QR por aeronave',
    'Checklist pre-aterrizaje',
  ],
  basic: [
    'Checklist pre-aterrizaje',
    'Firma digital',
    'Libro de vuelos',
    'Acceso QR por aeronave',
  ],
};

const AdminSubscription = ({ darkMode, orgFeatures }) => {
  const { t } = useTranslation('admin');
  const PLAN_FEATURES = getPlanFeatures(t);

  const [currentPlan, setCurrentPlan]   = useState('trial');
  const [subscription, setSubscription] = useState(null);
  const [userData, setUserData]         = useState(null);
  const [loading, setLoading]           = useState(true);
  const [upgrading, setUpgrading]       = useState(null);
  const [showUpgrade, setShowUpgrade]   = useState(false);
  const [showCancel, setShowCancel]     = useState(false);
  const [cancelling, setCancelling]     = useState(false);
  const [error, setError]               = useState('');
  const [voiceUsage, setVoiceUsage]     = useState(null);

  const [prices, setPrices] = useState({
    basic:      { usd: 30,  ars: 30000  },
    pro:        { usd: 80,  ars: 80000  },
    enterprise: { usd: 120, ars: 120000 },
  });

  const text    = darkMode ? 'rgba(255,255,255,0.9)'  : '#0f172a';
  const subtext = darkMode ? 'rgba(255,255,255,0.45)' : '#64748b';
  const cardBg  = darkMode ? 'rgba(255,255,255,0.04)' : 'white';
  const border  = darkMode ? 'rgba(255,255,255,0.08)' : '#e2e8f0';

  useEffect(() => {
    fetchAll();
    const handlePageShow = (e) => { if (e.persisted) setUpgrading(null); };
    window.addEventListener('pageshow', handlePageShow);
    return () => window.removeEventListener('pageshow', handlePageShow);
  }, []);

  const closeUpgradeModal = () => { setUpgrading(null); setShowUpgrade(false); };

  const fetchAll = async () => {
    try {
      const [meRes, settingsRes] = await Promise.allSettled([
        api.get('/auth/me'),
        api.get('/api/settings'),
      ]);

      if (meRes.status === 'fulfilled') {
        const user = meRes.value.data.user;
        setUserData(user);
        setCurrentPlan(user?.organization_plan || 'trial');

        const orgId = user?.organization_id;
        if (orgId) {
          try {
            const res = await api.get(`/api/payments/status/${orgId}`);
            setSubscription(res.data.subscription);
          } catch (_) {}
          try {
            const res = await api.get(`/api/organizations/${orgId}/voice-usage`);
            setVoiceUsage(res.data.data);
          } catch (_) {}
        }
      }

      if (settingsRes.status === 'fulfilled') {
        const d = settingsRes.value.data.data || {};
        setPrices({
          basic:      { usd: parseFloat(d.price_basic_usd || 30),  ars: parseFloat(d.price_basic_ars || 30000)  },
          pro:        { usd: parseFloat(d.price_pro_usd   || 80),  ars: parseFloat(d.price_pro_ars   || 80000)  },
          enterprise: { usd: 120, ars: 120000 },
        });
      }
    } catch (_) {}
    setLoading(false);
  };

  const handleUpgrade = async (planId) => {
    setUpgrading(planId); setError('');
    try {
      const res = await api.post('/api/payments/create-subscription', { plan: planId });
      if (res.data.init_point) window.location.href = res.data.init_point;
    } catch (err) {
      setError(err.response?.data?.message || 'Error al iniciar el pago');
      setUpgrading(null);
    }
  };

  const handleCancel = async () => {
    setCancelling(true);
    try {
      const meRes = await api.get('/auth/me');
      const orgId = meRes.data.user?.organization_id;
      await api.put(`/api/payments/cancel-subscription/${orgId}`);
      setShowCancel(false);
      fetchAll();
    } catch (err) { setError(err.response?.data?.message || 'Error al cancelar'); }
    setCancelling(false);
  };

  // ── Calcular días de trial restantes ─────────────────────────
  const getTrialDaysLeft = () => {
    if (!userData?.created_at) return null;
    const start   = new Date(userData.created_at);
    const trialEnd = new Date(start.getTime() + 30 * 24 * 60 * 60 * 1000);
    const today   = new Date();
    const diff    = Math.ceil((trialEnd - today) / (1000 * 60 * 60 * 24));
    return Math.max(0, diff);
  };

  const formatARS = (n) => n?.toLocaleString('es-AR', { minimumFractionDigits: 0, maximumFractionDigits: 0 });

  if (loading) {
    return (
      <div style={{ display:'flex', alignItems:'center', justifyContent:'center', minHeight:300, flexDirection:'column', gap:12 }}>
        <motion.div animate={{ rotate:360 }} transition={{ duration:1.2, repeat:Infinity, ease:'linear' }}
          style={{ width:36, height:36, borderRadius:'50%', border:'3px solid transparent', borderTopColor:'#2563eb', borderRightColor:'#7c3aed' }} />
        <p style={{ fontSize:13, color:subtext, margin:0 }}>Cargando suscripción...</p>
      </div>
    );
  }

  const planColor = { trial:'#f59e0b', basic:'#64748b', pro:'#2563eb', enterprise:'#7c3aed' };
  const color     = planColor[currentPlan] || '#64748b';
  const trialDaysLeft = currentPlan === 'trial' ? getTrialDaysLeft() : null;

  return (
    <div style={{ width: '100%' }}>

      {/* ── Header ── */}
      <div style={{ marginBottom:28 }}>
        <h2 style={{ margin:0, fontSize:26, fontWeight:900, color:text }}>Suscripción</h2>
        <p style={{ margin:'4px 0 0', fontSize:14, color:subtext }}>Gestioná tu plan y facturación</p>
      </div>

      {/* ── Plan actual ── */}
      <motion.div initial={{ opacity:0, y:12 }} animate={{ opacity:1, y:0 }}
        style={{ padding:24, borderRadius:22, background:cardBg, border:`1px solid ${border}`, marginBottom:16,
          boxShadow: darkMode?'none':'0 2px 12px rgba(0,0,0,0.06)' }}>

        {/* Header del plan */}
        <div style={{ display:'flex', alignItems:'center', gap:16, flexWrap:'wrap', marginBottom:20 }}>
          <div style={{ width:56, height:56, borderRadius:18, background:`${color}18`, display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>
            {currentPlan === 'pro' || currentPlan === 'enterprise'
              ? <Crown style={{ width:26, height:26, color }} />
              : <CreditCard style={{ width:26, height:26, color }} />
            }
          </div>
          <div style={{ flex:1 }}>
            <div style={{ display:'flex', alignItems:'center', gap:10, flexWrap:'wrap' }}>
              <h3 style={{ margin:0, fontSize:20, fontWeight:900, color:text }}>
                Plan {currentPlan.charAt(0).toUpperCase() + currentPlan.slice(1)}
              </h3>
              <span style={{ fontSize:11, fontWeight:800, padding:'3px 10px', borderRadius:20, background:`${color}18`, color, letterSpacing:'0.04em' }}>
                ● ACTIVO
              </span>
              {trialDaysLeft !== null && (
                <span style={{ fontSize:11, fontWeight:700, padding:'3px 10px', borderRadius:20,
                  background: trialDaysLeft <= 7 ? 'rgba(239,68,68,0.1)' : 'rgba(245,158,11,0.1)',
                  color: trialDaysLeft <= 7 ? '#dc2626' : '#d97706',
                  border: `1px solid ${trialDaysLeft <= 7 ? 'rgba(239,68,68,0.2)' : 'rgba(245,158,11,0.2)'}`,
                  display:'flex', alignItems:'center', gap:4 }}>
                  <Clock style={{ width:11, height:11 }} />
                  {trialDaysLeft === 0 ? 'Vence hoy' : `${trialDaysLeft} días restantes`}
                </span>
              )}
            </div>
            <p style={{ margin:'4px 0 0', fontSize:14, color:subtext }}>
              {currentPlan === 'trial'
                ? 'Gratis — período de prueba de 30 días'
                : `$${prices[currentPlan]?.usd} USD / $${formatARS(prices[currentPlan]?.ars)} ARS por mes`
              }
            </p>
          </div>
          {currentPlan !== 'pro' && currentPlan !== 'enterprise' && (
            <motion.button whileHover={{ scale:1.03, y:-1 }} whileTap={{ scale:0.97 }}
              onClick={() => setShowUpgrade(true)}
              style={{ display:'flex', alignItems:'center', gap:8, padding:'12px 22px', borderRadius:14, border:'none', cursor:'pointer',
                background:'linear-gradient(135deg,#2563eb,#1d4ed8)', color:'white', fontWeight:700, fontSize:14, fontFamily:'inherit',
                boxShadow:'0 4px 16px rgba(37,99,235,0.35)', whiteSpace:'nowrap' }}>
              <Zap style={{ width:16, height:16 }} /> Actualizar plan
            </motion.button>
          )}
        </div>

        {/* Separador */}
        <div style={{ height:1, background:border, marginBottom:18 }} />

        {/* Barra de uso de Valentina */}
        {voiceUsage && (
          <div style={{ marginBottom:18, padding:'14px 16px', borderRadius:16,
            background: darkMode?'rgba(139,92,246,0.06)':'rgba(139,92,246,0.04)',
            border: darkMode?'1px solid rgba(139,92,246,0.2)':'1px solid rgba(139,92,246,0.15)' }}>
            <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:8 }}>
              <div style={{ display:'flex', alignItems:'center', gap:7 }}>
                <span style={{ fontSize:16 }}>🎙️</span>
                <span style={{ fontSize:13, fontWeight:700, color:text }}>Voz Valentina — uso mensual</span>
              </div>
              <span style={{ fontSize:12, fontWeight:800,
                color: voiceUsage.pct >= 95 ? '#ef4444' : voiceUsage.pct >= 80 ? '#f59e0b' : '#8b5cf6' }}>
                {voiceUsage.pct}%
              </span>
            </div>
            <div style={{ height:8, borderRadius:99, background:darkMode?'rgba(255,255,255,0.08)':'#f1f5f9', overflow:'hidden', marginBottom:8 }}>
              <div style={{ height:'100%', borderRadius:99, transition:'width 0.6s ease',
                width:`${Math.min(voiceUsage.pct, 100)}%`,
                background: voiceUsage.pct >= 95 ? '#ef4444' : voiceUsage.pct >= 80 ? '#f59e0b' : 'linear-gradient(90deg,#8b5cf6,#6d28d9)' }} />
            </div>
            <div style={{ display:'flex', justifyContent:'space-between' }}>
              <span style={{ fontSize:11, color:subtext }}>
                {voiceUsage.used?.toLocaleString('es-AR')} caracteres usados
              </span>
              <span style={{ fontSize:11, color:subtext }}>
                {voiceUsage.limit === 999999 ? 'Ilimitado' : `${voiceUsage.remaining?.toLocaleString('es-AR')} restantes`}
              </span>
            </div>
            {voiceUsage.pct >= 80 && voiceUsage.limit !== 999999 && (
              <p style={{ margin:'8px 0 0', fontSize:12, fontWeight:600,
                color: voiceUsage.pct >= 95 ? '#ef4444' : '#d97706' }}>
                {voiceUsage.pct >= 95
                  ? '⛔ Valentina alcanzó el límite mensual. Actualizá el plan para continuar.'
                  : '⚠️ Quedan pocos caracteres de voz este mes. Considerá actualizar el plan.'}
              </p>
            )}
          </div>
        )}

        {/* Features incluidas */}
        <div style={{ marginBottom: PLAN_NOT_INCLUDED[currentPlan] ? 16 : 0 }}>
          <p style={{ margin:'0 0 12px', fontSize:12, fontWeight:700, textTransform:'uppercase', letterSpacing:'0.06em', color:subtext }}>
            Incluido en tu plan
          </p>
          <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill, minmax(220px, 1fr))', gap:8 }}>
            {(PLAN_FEATURES[currentPlan] || []).map((f, i) => (
              <div key={i} style={{ display:'flex', alignItems:'center', gap:8, fontSize:13, color: darkMode?'rgba(255,255,255,0.7)':'#374151' }}>
                <CheckCircle style={{ width:14, height:14, color:'#22c55e', flexShrink:0 }} />
                {f}
              </div>
            ))}
          </div>
        </div>

        {/* Features NO incluidas */}
        {PLAN_NOT_INCLUDED[currentPlan] && (
          <>
            <div style={{ height:1, background:border, margin:'16px 0' }} />
            <p style={{ margin:'0 0 12px', fontSize:12, fontWeight:700, textTransform:'uppercase', letterSpacing:'0.06em', color:subtext }}>
              No incluido en este plan
            </p>
            <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill, minmax(220px, 1fr))', gap:8 }}>
              {PLAN_NOT_INCLUDED[currentPlan].map((f, i) => (
                <div key={i} style={{ display:'flex', alignItems:'center', gap:8, fontSize:13, color: darkMode?'rgba(255,255,255,0.3)':'#94a3b8' }}>
                  <X style={{ width:13, height:13, color: darkMode?'rgba(255,255,255,0.2)':'#cbd5e1', flexShrink:0 }} />
                  {f}
                </div>
              ))}
            </div>
          </>
        )}

        {/* Estado suscripción MP */}
        {subscription?.status === 'active' && (
          <motion.div initial={{ opacity:0 }} animate={{ opacity:1 }}
            style={{ marginTop:16, padding:'12px 16px', borderRadius:14, background: darkMode?'rgba(34,197,94,0.08)':'rgba(34,197,94,0.05)', border:'1px solid rgba(34,197,94,0.2)', display:'flex', alignItems:'center', gap:8 }}>
            <Shield style={{ width:14, height:14, color:'#22c55e', flexShrink:0 }} />
            <span style={{ fontSize:13, fontWeight:600, color:'#22c55e' }}>Suscripción activa vía MercadoPago</span>
            {subscription.next_payment_date && (
              <span style={{ marginLeft:'auto', fontSize:12, color:subtext }}>
                Próximo cobro: {new Date(subscription.next_payment_date).toLocaleDateString('es-AR')}
              </span>
            )}
          </motion.div>
        )}
        {subscription?.status === 'cancelled' && (
          <div style={{ marginTop:16, padding:'12px 16px', borderRadius:14, background: darkMode?'rgba(100,116,139,0.08)':'rgba(100,116,139,0.05)', border:'1px solid rgba(100,116,139,0.2)' }}>
            <p style={{ margin:0, fontSize:13, fontWeight:600, color:'#64748b' }}>
              Suscripción cancelada
              {subscription.next_payment_date && ` — acceso activo hasta ${new Date(subscription.next_payment_date).toLocaleDateString('es-AR')}`}
            </p>
          </div>
        )}
      </motion.div>

      {/* ── Banner upgrade — solo si no es Pro/Enterprise ── */}
      {currentPlan !== 'pro' && currentPlan !== 'enterprise' && (
        <motion.div initial={{ opacity:0, y:8 }} animate={{ opacity:1, y:0 }} transition={{ delay:0.1 }}
          style={{ padding:20, borderRadius:20, marginBottom:16,
            background: darkMode ? 'linear-gradient(135deg,rgba(37,99,235,0.12),rgba(124,58,237,0.1))' : 'linear-gradient(135deg,rgba(37,99,235,0.06),rgba(124,58,237,0.04))',
            border: darkMode ? '1px solid rgba(37,99,235,0.25)' : '1px solid rgba(37,99,235,0.18)' }}>
          <div style={{ display:'flex', alignItems:'flex-start', justifyContent:'space-between', gap:16, flexWrap:'wrap' }}>
            <div style={{ flex:1, minWidth:200 }}>
              <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:8 }}>
                <Crown style={{ width:18, height:18, color:'#2563eb' }} />
                <p style={{ margin:0, fontSize:15, fontWeight:800, color: darkMode?'#93c5fd':'#1d4ed8' }}>
                  Desbloqueá el Plan Pro
                </p>
              </div>
              <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill, minmax(180px, 1fr))', gap:6 }}>
                {['Valentina IA ilimitada', 'Squawks y AOG avanzado', 'Firma digital + PDF', 'Libro de vuelos', 'Acceso QR por aeronave'].map((f, i) => (
                  <div key={i} style={{ display:'flex', alignItems:'center', gap:6, fontSize:12, color: darkMode?'rgba(147,197,253,0.85)':'#2563eb' }}>
                    <Check style={{ width:12, height:12, flexShrink:0 }} />
                    {f}
                  </div>
                ))}
              </div>
            </div>
            <motion.button whileHover={{ scale:1.04, y:-1 }} whileTap={{ scale:0.97 }}
              onClick={() => setShowUpgrade(true)}
              style={{ display:'flex', alignItems:'center', gap:8, padding:'11px 20px', borderRadius:14, border:'none', cursor:'pointer',
                background:'linear-gradient(135deg,#2563eb,#7c3aed)', color:'white', fontWeight:700, fontSize:13, fontFamily:'inherit',
                boxShadow:'0 4px 14px rgba(37,99,235,0.3)', whiteSpace:'nowrap', flexShrink:0 }}>
              <Zap style={{ width:14, height:14 }} />
              Ver planes
            </motion.button>
          </div>
        </motion.div>
      )}

      {/* ── Error ── */}
      <AnimatePresence>
        {error && (
          <motion.div initial={{ opacity:0, y:-8 }} animate={{ opacity:1, y:0 }} exit={{ opacity:0 }}
            style={{ marginBottom:16, padding:'12px 16px', borderRadius:14, background:'rgba(239,68,68,0.06)', border:'1px solid rgba(239,68,68,0.18)', display:'flex', alignItems:'center', gap:8 }}>
            <AlertCircle style={{ width:16, height:16, color:'#ef4444', flexShrink:0 }} />
            <p style={{ margin:0, fontSize:13, color:'#ef4444', fontWeight:600 }}>{error}</p>
            <button onClick={() => setError('')} style={{ marginLeft:'auto', background:'none', border:'none', cursor:'pointer', color:'#ef4444' }}>
              <X style={{ width:14, height:14 }} />
            </button>
          </motion.div>
        )}
      </AnimatePresence>

      {/* ── Contacto y soporte ── */}
      <motion.div initial={{ opacity:0, y:8 }} animate={{ opacity:1, y:0 }} transition={{ delay:0.15 }}
        style={{ padding:20, borderRadius:20, background:cardBg, border:`1px solid ${border}`,
          boxShadow: darkMode?'none':'0 2px 8px rgba(0,0,0,0.04)', marginBottom: subscription?.status === 'active' ? 16 : 0 }}>
        <p style={{ margin:'0 0 14px', fontSize:13, fontWeight:700, textTransform:'uppercase', letterSpacing:'0.06em', color:subtext }}>
          Soporte y consultas
        </p>
        <div style={{ display:'flex', flexDirection:'column', gap:10 }}>
          <a href="mailto:soporte@condor360.app"
            style={{ display:'flex', alignItems:'center', gap:12, padding:'12px 16px', borderRadius:14,
              background: darkMode?'rgba(255,255,255,0.04)':'#f8fafc', border:`1px solid ${border}`,
              textDecoration:'none', transition:'all 0.15s' }}
            onMouseEnter={e => e.currentTarget.style.borderColor = '#2563eb'}
            onMouseLeave={e => e.currentTarget.style.borderColor = border}>
            <div style={{ width:36, height:36, borderRadius:10, background:'rgba(37,99,235,0.1)', display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>
              <Mail style={{ width:16, height:16, color:'#2563eb' }} />
            </div>
            <div>
              <p style={{ margin:0, fontSize:13, fontWeight:700, color:text }}>soporte@condor360.app</p>
              <p style={{ margin:0, fontSize:12, color:subtext }}>Consultas sobre planes, facturación o soporte técnico</p>
            </div>
            <ArrowRight style={{ width:14, height:14, color:subtext, marginLeft:'auto', flexShrink:0 }} />
          </a>
          <a href="https://wa.me/5491155691805" target="_blank" rel="noopener noreferrer"
            style={{ display:'flex', alignItems:'center', gap:12, padding:'12px 16px', borderRadius:14,
              background: darkMode?'rgba(255,255,255,0.04)':'#f8fafc', border:`1px solid ${border}`,
              textDecoration:'none', transition:'all 0.15s' }}
            onMouseEnter={e => e.currentTarget.style.borderColor = '#16a34a'}
            onMouseLeave={e => e.currentTarget.style.borderColor = border}>
            <div style={{ width:36, height:36, borderRadius:10, background:'rgba(22,163,74,0.1)', display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>
              <MessageCircle style={{ width:16, height:16, color:'#16a34a' }} />
            </div>
            <div style={{ flex:1 }}>
              <p style={{ margin:0, fontSize:13, fontWeight:700, color:text }}>+54 9 11 5569-1805</p>
              <p style={{ margin:0, fontSize:12, color:subtext }}>WhatsApp · Lunes a viernes · Respuesta en menos de 24hs</p>
            </div>
            <ArrowRight style={{ width:14, height:14, color:subtext, flexShrink:0 }} />
          </a>
        </div>
      </motion.div>

      {/* ── Cancelar suscripción ── */}
      {subscription?.status === 'active' && (
        <div style={{ textAlign:'center', marginTop:16 }}>
          <button onClick={() => setShowCancel(true)}
            style={{ padding:'10px 24px', borderRadius:12, border:'1px solid rgba(239,68,68,0.25)', background:'transparent', cursor:'pointer',
              fontFamily:'inherit', fontSize:13, fontWeight:600, color:'#ef4444' }}>
            Cancelar suscripción
          </button>
        </div>
      )}

      {/* ════════════════════════
          MODAL: Elegir plan
      ════════════════════════ */}
      <AnimatePresence>
        {showUpgrade && (
          <motion.div initial={{ opacity:0 }} animate={{ opacity:1 }} exit={{ opacity:0 }}
            style={{ position:'fixed', inset:0, zIndex:50, display:'flex', alignItems:'center', justifyContent:'center', padding:16, background:'rgba(0,0,0,0.65)', backdropFilter:'blur(8px)' }}
            onClick={closeUpgradeModal}>
            <motion.div initial={{ scale:0.92, y:24 }} animate={{ scale:1, y:0 }} exit={{ scale:0.92, y:24 }}
              transition={{ type:'spring', stiffness:140, damping:18 }}
              style={{ width:'100%', maxWidth:720, borderRadius:28, background: darkMode?'#111827':'white', boxShadow:'0 32px 80px rgba(0,0,0,0.4)', overflow:'hidden' }}
              onClick={e => e.stopPropagation()}>

              <div style={{ padding:'24px 28px 20px', borderBottom:`1px solid ${border}`, display:'flex', alignItems:'center', justifyContent:'space-between' }}>
                <div>
                  <h3 style={{ margin:0, fontSize:20, fontWeight:900, color:text }}>Elegí tu plan</h3>
                  <p style={{ margin:'3px 0 0', fontSize:13, color:subtext }}>Todos los planes incluyen soporte en español</p>
                </div>
                <button onClick={closeUpgradeModal}
                  style={{ width:34, height:34, borderRadius:10, background: darkMode?'rgba(255,255,255,0.08)':'#f1f5f9', border:'none', cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center', color:subtext }}>
                  <X style={{ width:18, height:18 }} />
                </button>
              </div>

              <div style={{ padding:'24px 28px', display:'grid', gridTemplateColumns:'repeat(3, 1fr)', gap:14 }}>
                {['basic','pro','enterprise'].map(planId => {
                  const isCurrent     = planId === currentPlan;
                  const isDisabled    = planId === 'enterprise';
                  const isLoading     = upgrading === planId;
                  const pColor        = planColor[planId];
                  const isRecommended = planId === 'pro';

                  return (
                    <motion.div key={planId}
                      whileHover={!isCurrent && !isDisabled ? { y:-4, boxShadow:`0 12px 32px ${pColor}25` } : {}}
                      style={{ borderRadius:20, padding:20, position:'relative',
                        cursor: isDisabled ? 'default' : 'pointer',
                        border: isRecommended ? `2px solid ${pColor}` : `1.5px solid ${darkMode?'rgba(255,255,255,0.1)':'#e2e8f0'}`,
                        background: isRecommended ? (darkMode?`${pColor}0d`:`${pColor}06`) : (darkMode?'rgba(255,255,255,0.03)':'#fafafa'),
                        opacity: isDisabled ? 0.55 : 1,
                        transition:'all 0.2s' }}>

                      {isRecommended && !isDisabled && (
                        <div style={{ position:'absolute', top:-11, left:'50%', transform:'translateX(-50%)', background:`linear-gradient(135deg,${pColor},${pColor}cc)`, color:'white', fontSize:9, fontWeight:800, padding:'3px 12px', borderRadius:20, whiteSpace:'nowrap', letterSpacing:'0.06em' }}>
                          MÁS ELEGIDO
                        </div>
                      )}
                      {isDisabled && (
                        <div style={{ position:'absolute', top:-11, left:'50%', transform:'translateX(-50%)', background:'#64748b', color:'white', fontSize:9, fontWeight:800, padding:'3px 12px', borderRadius:20, whiteSpace:'nowrap', letterSpacing:'0.06em' }}>
                          PRÓXIMAMENTE
                        </div>
                      )}

                      <div style={{ marginTop:8, marginBottom:14 }}>
                        <p style={{ margin:'0 0 8px', fontSize:15, fontWeight:900, color:text }}>
                          {planId.charAt(0).toUpperCase() + planId.slice(1)}
                        </p>
                        {isDisabled ? (
                          <p style={{ margin:0, fontSize:22, fontWeight:900, color:pColor, lineHeight:1 }}>Q3 2026</p>
                        ) : (
                          <>
                            <p style={{ margin:0, fontSize:28, fontWeight:900, color:pColor, lineHeight:1 }}>
                              <span style={{ fontSize:15, fontWeight:600, color:subtext }}>USD </span>
                              ${prices[planId]?.usd}
                              <span style={{ fontSize:12, fontWeight:500, color:subtext }}>/mes</span>
                            </p>
                            <p style={{ margin:'4px 0 0', fontSize:12, color:subtext }}>
                              ${formatARS(prices[planId]?.ars)} ARS/mes
                            </p>
                          </>
                        )}
                      </div>

                      <div style={{ display:'flex', flexDirection:'column', gap:5, marginBottom:16, minHeight:110 }}>
                        {PLAN_FEATURES[planId]?.map((f, i) => (
                          <div key={i} style={{ display:'flex', alignItems:'flex-start', gap:6, fontSize:12, color:subtext, lineHeight:1.4 }}>
                            <Check style={{ width:12, height:12, color:'#22c55e', flexShrink:0, marginTop:1 }} />
                            {f}
                          </div>
                        ))}
                      </div>

                      {isCurrent ? (
                        <div style={{ padding:'10px', borderRadius:12, textAlign:'center', fontSize:13, fontWeight:700, color:'#22c55e', background:'rgba(34,197,94,0.1)', border:'1px solid rgba(34,197,94,0.2)' }}>
                          ✓ Plan actual
                        </div>
                      ) : (
                        <motion.button
                          whileHover={!isDisabled && !upgrading ? { scale:1.03 } : {}}
                          whileTap={!isDisabled && !upgrading ? { scale:0.97 } : {}}
                          disabled={isDisabled || !!upgrading}
                          onClick={() => !isDisabled && handleUpgrade(planId)}
                          style={{ width:'100%', padding:'11px', borderRadius:12, border:'none', fontFamily:'inherit', fontWeight:700, fontSize:13,
                            cursor: isDisabled || upgrading ? 'not-allowed' : 'pointer',
                            background: isDisabled ? (darkMode?'rgba(255,255,255,0.05)':'#e2e8f0') : `linear-gradient(135deg,${pColor},${pColor}cc)`,
                            color: isDisabled ? subtext : 'white',
                            display:'flex', alignItems:'center', justifyContent:'center', gap:6,
                            boxShadow: !isDisabled ? `0 4px 14px ${pColor}35` : 'none' }}>
                          {isLoading
                            ? <><motion.div animate={{ rotate:360 }} transition={{ duration:1, repeat:Infinity, ease:'linear' }}
                                style={{ width:14, height:14, borderRadius:'50%', border:'2px solid transparent', borderTopColor:'white' }} />Procesando...</>
                            : isDisabled ? t('coming_soon')
                            : <><ArrowRight style={{ width:14, height:14 }} />Elegir plan</>
                          }
                        </motion.button>
                      )}
                    </motion.div>
                  );
                })}
              </div>

              <div style={{ padding:'16px 28px', borderTop:`1px solid ${border}`, display:'flex', alignItems:'center', gap:12 }}>
                <Shield style={{ width:14, height:14, color:'#22c55e', flexShrink:0 }} />
                <p style={{ margin:0, fontSize:12, color:subtext }}>
                  Pago seguro vía MercadoPago. Sin contratos. Cancelás cuando querés.
                </p>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* ════════════════════════
          MODAL: Confirmar cancelación
      ════════════════════════ */}
      <AnimatePresence>
        {showCancel && (
          <motion.div initial={{ opacity:0 }} animate={{ opacity:1 }} exit={{ opacity:0 }}
            style={{ position:'fixed', inset:0, zIndex:50, display:'flex', alignItems:'center', justifyContent:'center', padding:16, background:'rgba(0,0,0,0.65)', backdropFilter:'blur(8px)' }}
            onClick={() => !cancelling && setShowCancel(false)}>
            <motion.div initial={{ scale:0.92, y:24 }} animate={{ scale:1, y:0 }} exit={{ scale:0.92, y:24 }}
              transition={{ type:'spring', stiffness:140, damping:18 }}
              style={{ width:'100%', maxWidth:400, borderRadius:28, background: darkMode?'#111827':'white', padding:28, boxShadow:'0 32px 80px rgba(0,0,0,0.4)' }}
              onClick={e => e.stopPropagation()}>

              <div style={{ width:56, height:56, borderRadius:18, background:'rgba(239,68,68,0.1)', display:'flex', alignItems:'center', justifyContent:'center', margin:'0 auto 16px' }}>
                <AlertCircle style={{ width:26, height:26, color:'#ef4444' }} />
              </div>

              <h3 style={{ margin:'0 0 8px', fontSize:18, fontWeight:800, textAlign:'center', color:text }}>
                {currentPlan === 'trial' ? t('no_active_subscription') : t('cancel_plan_confirm',{plan:currentPlan.charAt(0).toUpperCase()+currentPlan.slice(1)})}
              </h3>
              <p style={{ margin:'0 0 6px', fontSize:14, textAlign:'center', color:subtext, lineHeight:1.6 }}>
                {currentPlan === 'trial'
                  ? 'No tenés una suscripción paga activa para cancelar.'
                  : subscription?.next_payment_date
                    ? `Seguís teniendo acceso hasta el ${new Date(subscription.next_payment_date).toLocaleDateString('es-AR')}. Después tu cuenta vuelve al plan Trial.`
                    : 'Tu cuenta volverá al plan Trial de inmediato.'}
              </p>

              {PLAN_NOT_INCLUDED[currentPlan] && currentPlan !== 'trial' && (
                <div style={{ margin:'16px 0', padding:'12px 16px', borderRadius:14, background: darkMode?'rgba(239,68,68,0.06)':'rgba(239,68,68,0.04)', border:'1px solid rgba(239,68,68,0.15)' }}>
                  <p style={{ margin:'0 0 8px', fontSize:11, fontWeight:800, color:'#ef4444', textTransform:'uppercase', letterSpacing:'0.06em' }}>Perdés acceso a:</p>
                  <div style={{ display:'flex', flexDirection:'column', gap:5 }}>
                    {(PLAN_FEATURES[currentPlan] || []).filter(f => f !== 'Todo de Basic').map((f, i) => (
                      <div key={i} style={{ display:'flex', alignItems:'center', gap:6, fontSize:12, color: darkMode?'rgba(255,255,255,0.45)':'#64748b' }}>
                        <X style={{ width:11, height:11, color:'#ef4444', flexShrink:0 }} />
                        {f}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div style={{ display:'flex', gap:10, marginTop:20 }}>
                <button onClick={() => setShowCancel(false)} disabled={cancelling}
                  style={{ flex:1, padding:13, borderRadius:14, border: darkMode?'1px solid rgba(255,255,255,0.1)':'1px solid #e2e8f0', background:'transparent', cursor:'pointer', fontFamily:'inherit', fontWeight:700, fontSize:14, color:subtext }}>
                  Mantener plan
                </button>
                <motion.button onClick={handleCancel} disabled={cancelling}
                  whileHover={!cancelling?{ scale:1.02 }:{}} whileTap={!cancelling?{ scale:0.97 }:{}}
                  style={{ flex:1, padding:13, borderRadius:14, border:'none', cursor:cancelling?'not-allowed':'pointer', fontFamily:'inherit', fontWeight:700, fontSize:14, color:'white', background:'linear-gradient(135deg,#ef4444,#dc2626)', display:'flex', alignItems:'center', justifyContent:'center', gap:6, boxShadow:'0 4px 14px rgba(239,68,68,0.35)' }}>
                  {cancelling
                    ? <><motion.div animate={{ rotate:360 }} transition={{ duration:1, repeat:Infinity, ease:'linear' }} style={{ width:15, height:15, borderRadius:'50%', border:'2px solid transparent', borderTopColor:'white' }} />Cancelando...</>
                    : 'Sí, cancelar'
                  }
                </motion.button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <style>{`@keyframes spin { from{transform:rotate(0deg)} to{transform:rotate(360deg)} }`}</style>
    </div>
  );
};

export default AdminSubscription;
